import React, { useEffect, useState } from "react";
import { Modal, message } from "antd";
import { useSelector, useDispatch } from "react-redux";
import { setSelectedRole, updateCurrentRole } from "../store/auth";
import axios from "axios";
import {
  UserSwitchOutlined,
  RightOutlined,
  DashboardOutlined,
  TeamOutlined,
  CustomerServiceOutlined,
  UserOutlined,
  BankOutlined,
} from "@ant-design/icons";
import "./RoleSelectionModal.scss";

/* What each workspace actually opens onto, so the choice is informed rather
   than a list of job titles. */
const ROLE_BLURB = {
  supervisor: "Team dashboards, allocation and agency setup",
  team_lead: "Your team's cases, targets and performance",
  telecaller: "Your call queue, PTPs and callbacks",
  field_agent: "Assigned visits and field feedback",
  agency: "Portfolios, campaigns, reports and users",
};

const RoleSelectionModal = () => {
  const { user, token } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [selectedRoleData, setSelectedRoleData] = useState(null);

  useEffect(() => {
    if (!user || !user.idToken) {
      setOpen(false);
      return;
    }

    // Identify all true roles for the user
    const roles = [];
    if (user.is_supervisor) roles.push({ key: "supervisor", label: "Supervisor", icon: <DashboardOutlined /> });
    if (user.is_tl) roles.push({ key: "team_lead", label: "Team Lead", icon: <TeamOutlined /> });
    if (user.is_tc) roles.push({ key: "telecaller", label: "Telecaller", icon: <CustomerServiceOutlined /> });
    if (user.is_fa) roles.push({ key: "field_agent", label: "Field Agent", icon: <UserOutlined /> });
    if (user.is_agency) roles.push({ key: "agency", label: "Agency", icon: <BankOutlined /> });

    // If user has no selected role yet...
    if (!user.selectedRole) {
      if (roles.length > 1) {
        // Multiple roles available -> Force selection
        setAvailableRoles(roles);
        setOpen(true);
      } else if (roles.length === 1) {
        // Only one role available -> Auto select (background)
        dispatch(updateCurrentRole(roles[0].key)).unwrap().then(() => {
          if (roles[0].key === 'telecaller') {
            axios.post('auth/attendance/action/', {}, { headers: { Authorization: `Bearer ${token}` } })
              .then(res => console.log('Attendance Auto Hit:', res.data))
              .catch(err => console.error('Attendance Auto Hit Error:', err));
          }
          dispatch(setSelectedRole(roles[0].key));
        }).catch(() => {
          // Fallback even if api fails
          dispatch(setSelectedRole(roles[0].key));
        });
      }
    } else {
      // Role already selected
      setOpen(false);
    }
  }, [user, dispatch]);

  const handleRoleSelect = async (role) => {
    setIsRedirecting(true);
    setSelectedRoleData(role);
    try {
      await dispatch(updateCurrentRole(role.key)).unwrap();

      if (role.key === 'telecaller') {
        axios.post('auth/attendance/action/', {}, { headers: { Authorization: `Bearer ${token}` } })
          .then(res => console.log('Attendance Manual Hit:', res.data))
          .catch(err => console.error('Attendance Manual Hit Error:', err));
      }

      // Artificial short delay for smooth UX transition
      setTimeout(() => {
        dispatch(setSelectedRole(role.key));
        setOpen(false);
        setIsRedirecting(false);
      }, 600);

    } catch (error) {
      message.error("Failed to set workspace. Please try again.");
      setIsRedirecting(false);
      setSelectedRoleData(null);
    }
  };

  return (
    <Modal
      open={open}
      closable={false}
      maskClosable={false}
      keyboard={false}
      footer={null}
      centered
      width={472}
      className="role-selection-modal"
    >
      {/* Both states share one shell - a brand band over a light body - so
          picking a workspace and waiting for it read as one screen rather
          than two unrelated dialogs. */}
      <div className={`roleShell ${isRedirecting ? "is-busy" : ""}`}>
        <header className="roleShell__hero">
          <span className="roleShell__badge">
            {isRedirecting ? selectedRoleData?.icon : <UserSwitchOutlined />}
          </span>

          <h3>
            {isRedirecting ? "Opening your workspace" : "Select workspace"}
          </h3>

          <p>
            {isRedirecting
              ? "Setting things up — this only takes a moment."
              : "Your account holds more than one role. Pick the one you want to work in for this session."}
          </p>

          {!isRedirecting && user?.email && (
            <span className="roleShell__who">Signed in as {user.email}</span>
          )}
        </header>

        <div className="roleShell__body">
          {isRedirecting ? (
            <div className="roleGo">
              <span className="roleGo__bar" aria-hidden="true">
                <i />
              </span>
              <p className="roleGo__note">
                Preparing <b>{selectedRoleData?.label}</b>
              </p>
            </div>
          ) : (
            <>
              <div className="rolePick__list">
                {availableRoles.map((role, index) => (
                  /* was a div with onClick and two JS handlers mutating inline
                     styles - a real button is focusable and hovers in CSS */
                  <button
                    type="button"
                    key={role.key}
                    className="roleCard"
                    style={{ "--i": index }}
                    onClick={() => handleRoleSelect(role)}
                  >
                    <span className="roleCard__icon">{role.icon}</span>
                    <span className="roleCard__text">
                      <b>{role.label}</b>
                      {ROLE_BLURB[role.key] && <i>{ROLE_BLURB[role.key]}</i>}
                    </span>
                    <RightOutlined className="roleCard__chevron" />
                  </button>
                ))}
              </div>

              <p className="rolePick__foot">
                You can switch workspace later from your profile menu.
              </p>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default RoleSelectionModal;
